import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquare, Send, Lock, Plus, Trash2, Users, CalendarDays, Newspaper, ExternalLink, BarChart3, Eye, CalendarCheck, XCircle, LogOut, ChevronDown } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Logo } from "@/components/Logo";
import { Checkbox } from "@/components/ui/checkbox";
import { EVENT_TYPES } from "@/data";
import AdminQuiz from "@/components/AdminQuiz";
import AdminMembers from "@/components/AdminMembers";
import AdminQr from "@/components/AdminQr";
import AdminTeacherEval from "@/components/AdminTeacherEval";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseList = (str) =>
  str.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
const uniq = (arr) => [...new Set(arr)];

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
);

const inputCls =
  "w-full bg-white border border-border rounded-sm px-4 py-3 text-sm outline-none focus:border-primary transition-colors";
const labelCls = "font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground";

export default function Admin() {
  const location = useLocation();
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const processed = useRef(false);

  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState({});
  const [newC, setNewC] = useState({ name: "", email: "", phone: "" });

  const [events, setEvents] = useState([]);
  const [newE, setNewE] = useState({ title: "", date: "", type: "Εκδήλωση", note: "" });

  const [eduNews, setEduNews] = useState([]);
  const [newEN, setNewEN] = useState({ title: "", summary: "", url: "", source: "", category: "Νέα" });

  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(true);
  const [extraEmails, setExtraEmails] = useState("");
  const [extraPhones, setExtraPhones] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const authHeader = { headers: { "x-admin-token": token } };

  const loadContacts = async () => {
    const { data } = await axios.get(`${API}/admin/contacts`, authHeader);
    setContacts(data);
  };

  const loadEvents = async () => {
    const { data } = await axios.get(`${API}/events`);
    setEvents(data);
  };

  const loadAppts = async () => {
    const { data } = await axios.get(`${API}/admin/appointments`, authHeader);
    setAppts(data);
  };

  const cancelAppt = async (id) => {
    if (!window.confirm("Ακύρωση ραντεβού; Η ώρα θα ελευθερωθεί.")) return;
    await axios.delete(`${API}/admin/appointments/${id}`, authHeader);
    toast.success("Το ραντεβού ακυρώθηκε.");
    await loadAppts();
  };

  const loadEduNews = async () => {
    const { data } = await axios.get(`${API}/edu-news`);
    setEduNews(data);
  };

  const loadStats = async () => {
    const { data } = await axios.get(`${API}/admin/stats`, authHeader);
    setStats(data);
  };

  const loadAll = async (hdr) => {
    await Promise.all([
      axios.get(`${API}/admin/contacts`, hdr).then((r) => setContacts(r.data)),
      loadEvents(), loadEduNews(),
      axios.get(`${API}/admin/stats`, hdr).then((r) => setStats(r.data)),
      axios.get(`${API}/admin/appointments`, hdr).then((r) => setAppts(r.data)),
    ]);
  };

  const enter = async (sessionToken, user) => {
    setToken(sessionToken);
    await loadAll({ headers: { "x-admin-token": sessionToken } });
    setAdminUser(user);
    setAuthed(true);
  };

  // Google callback: session_id arrives in the URL fragment → exchange it on the backend
  useEffect(() => {
    const hash = location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      try {
        const { data } = await axios.post(`${API}/admin/auth/session`, { session_id: sid }, { withCredentials: true });
        await enter(data.session_token, data);
        toast.success(`Σύνδεση ως ${data.email}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Αποτυχία σύνδεσης Google.");
      } finally { setChecking(false); }
    })();
  }, [location.hash]); // eslint-disable-line

  // Existing 7-day session (httpOnly cookie)
  useEffect(() => {
    if ((location.hash || "").includes("session_id=")) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/auth/me`, { withCredentials: true });
        if (data.session_token) await enter(data.session_token, data);
      } catch (e) { /* not logged in */ } finally { setChecking(false); }
    })();
  }, []); // eslint-disable-line

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin/auth/logout`, {}, { ...authHeader, withCredentials: true }); } catch (e) { /* ignore */ }
    setAuthed(false); setAdminUser(null); setToken("");
    toast.success("Αποσυνδεθήκατε.");
  };

  const onLogin = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Εισάγετε τον κωδικό διαχειριστή.");
    setLoadingAuth(true);
    try {
      await enter(token, { email: "", name: "Διεύθυνση (κωδικός)", via: "password" });
      toast.success("Σύνδεση επιτυχής.");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Λάθος κωδικός." : "Αποτυχία σύνδεσης.");
    } finally {
      setLoadingAuth(false);
    }
  };

  const addEvent = async (e) => {
    e.preventDefault();
    if (!newE.title.trim() || !newE.date) return toast.error("Συμπληρώστε τίτλο & ημερομηνία.");
    try {
      await axios.post(`${API}/admin/events`, newE, authHeader);
      setNewE({ title: "", date: "", type: "Εκδήλωση", note: "" });
      await loadEvents();
      toast.success("Η εκδήλωση προστέθηκε.");
    } catch {
      toast.error("Αποτυχία προσθήκης.");
    }
  };

  const removeEvent = async (id) => {
    try {
      await axios.delete(`${API}/admin/events/${id}`, authHeader);
      await loadEvents();
    } catch {
      toast.error("Αποτυχία διαγραφής.");
    }
  };

  const addEduNews = async (e) => {
    e.preventDefault();
    if (!newEN.title.trim()) return toast.error("Συμπληρώστε τίτλο.");
    try {
      await axios.post(`${API}/admin/edu-news`, newEN, authHeader);
      setNewEN({ title: "", summary: "", url: "", source: "", category: "Νέα" });
      await loadEduNews();
      toast.success("Το άρθρο προστέθηκε.");
    } catch {
      toast.error("Αποτυχία προσθήκης.");
    }
  };

  const removeEduNews = async (id) => {
    try {
      await axios.delete(`${API}/admin/edu-news/${id}`, authHeader);
      await loadEduNews();
    } catch {
      toast.error("Αποτυχία διαγραφής.");
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    if (!newC.email && !newC.phone) return toast.error("Συμπληρώστε email ή τηλέφωνο.");
    try {
      await axios.post(`${API}/admin/contacts`, newC, authHeader);
      setNewC({ name: "", email: "", phone: "" });
      await loadContacts();
      toast.success("Η επαφή προστέθηκε.");
    } catch {
      toast.error("Αποτυχία προσθήκης.");
    }
  };

  const removeContact = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader);
      setSelected((s) => { const c = { ...s }; delete c[id]; return c; });
      await loadContacts();
    } catch {
      toast.error("Αποτυχία διαγραφής.");
    }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedContacts = contacts.filter((c) => selected[c.id]);
  const [contactFilter, setContactFilter] = useState("all");
  const CONTACT_FILTERS = [["all", "Όλες"], ["Μαθητής", "Μαθητές"], ["Γονέας", "Γονείς"], ["Καθηγητής", "Καθηγητές"], ["other", "Λοιπές"]];
  const matchesFilter = (c) => contactFilter === "all" || (contactFilter === "other" ? !c.source : c.source === contactFilter);
  const visibleContacts = contacts.filter(matchesFilter);
  const countFor = (f) => contacts.filter((c) => f === "all" || (f === "other" ? !c.source : c.source === f)).length;
  const selectAll = () => setSelected((s) => ({ ...s, ...Object.fromEntries(visibleContacts.map((c) => [c.id, true])) }));
  const clearAll = () => setSelected({});

  const onSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Γράψτε το μήνυμα.");
    const channels = [];
    if (email) channels.push("email");
    if (sms) channels.push("sms");
    if (channels.length === 0) return toast.error("Επιλέξτε τουλάχιστον ένα κανάλι.");

    const emails = uniq([...selectedContacts.map((c) => c.email).filter(Boolean), ...parseList(extraEmails)]);
    const phones = uniq([...selectedContacts.map((c) => c.phone).filter(Boolean), ...parseList(extraPhones)]);
    if (email && emails.length === 0) return toast.error("Δεν υπάρχουν παραλήπτες email.");
    if (sms && phones.length === 0) return toast.error("Δεν υπάρχουν αριθμοί τηλεφώνου.");

    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/broadcast`, { subject, message, channels, emails, phones }, authHeader);
      setResult(data);
      toast.success("Η αποστολή ολοκληρώθηκε.");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Λάθος κωδικός." : "Αποτυχία αποστολής.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-secondary/40 flex items-center justify-center" data-testid="admin-checking"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-secondary/40 font-body flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          data-testid="admin-login"
          className="bg-white border border-border rounded-sm p-8 w-full max-w-sm"
        >
          <div className="flex items-center gap-3 mb-6">
            <Logo className="h-10 w-12" ringColor="#0A1A4A" />
            <div>
              <div className="font-heading font-extrabold text-lg leading-none">Φυσική Επιλογή</div>
              <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">Διαχείριση</div>
            </div>
          </div>
          <button data-testid="admin-google-login" type="button" onClick={googleLogin} className="w-full inline-flex items-center justify-center gap-3 bg-white border-2 border-foreground text-foreground font-mono-accent text-sm uppercase tracking-widest px-6 py-3.5 rounded-sm hover:bg-foreground hover:text-white transition-colors">
            <GoogleIcon /> Σύνδεση με Google
          </button>
          <p className="mt-3 text-xs text-muted-foreground text-center">Μόνο για εξουσιοδοτημένους λογαριασμούς Google του φροντιστηρίου. Η σύνδεση διατηρείται για 7 ημέρες.</p>

          <button data-testid="admin-toggle-password" type="button" onClick={() => setShowPw(!showPw)} className="mt-6 w-full inline-flex items-center justify-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary">
            <Lock className="h-3 w-3" /> Σύνδεση με κωδικό <ChevronDown className={`h-3 w-3 transition-transform ${showPw ? "rotate-180" : ""}`} />
          </button>
          {showPw && (
            <form onSubmit={onLogin} data-testid="admin-password-form" className="mt-3">
              <input data-testid="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} className={inputCls} placeholder="Κωδικός διαχειριστή" />
              <button data-testid="admin-login-btn" type="submit" disabled={loadingAuth} className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-accent text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-accent transition-colors disabled:opacity-60">
                {loadingAuth && <Loader2 className="h-4 w-4 animate-spin" />} Σύνδεση
              </button>
            </form>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/40 font-body">
      <header className="bg-foreground text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-20 flex items-center gap-3">
          <Logo className="h-9 w-11" ringColor="#FFFFFF" />
          <div>
            <div className="font-heading font-extrabold text-lg leading-none">Φυσική Επιλογή</div>
            <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">Πίνακας Αποστολής</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {adminUser?.picture && <img src={adminUser.picture} alt="" className="h-8 w-8 rounded-full border border-white/30" referrerPolicy="no-referrer" />}
            <span data-testid="admin-user-email" className="hidden sm:block font-mono-accent text-[10px] tracking-widest text-white/70">{adminUser?.email || adminUser?.name}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 font-mono-accent text-[10px] uppercase tracking-widest border border-white/30 px-3 py-2 rounded-sm hover:bg-white hover:text-foreground transition-colors"><LogOut className="h-3.5 w-3.5" /> Έξοδος</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-5 gap-8">
        {/* Visit stats */}
        <section className="lg:col-span-5" data-testid="stats-section">
          <div className="mb-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Στατιστικά</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="h-6 w-6 text-primary" />Επισκέψεις ιστοσελίδας</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Σύνολο" value={stats?.total} unique={stats?.unique_total} testid="stat-total" />
            <StatCard label="Σήμερα" value={stats?.today} unique={stats?.unique_today} testid="stat-today" />
            <StatCard label="7 ημέρες" value={stats?.last7} unique={stats?.unique_7} testid="stat-7" />
            <StatCard label="30 ημέρες" value={stats?.last30} unique={stats?.unique_30} testid="stat-30" />
          </div>

          <div className="bg-white border border-border rounded-sm p-5">
            <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Επισκέψεις ανά ημέρα (τελευταίες 30)</div>
            <div style={{ width: "100%", height: 220 }} data-testid="stats-chart">
              <ResponsiveContainer>
                <BarChart data={stats?.daily || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="day" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10, fill: "#6B7280" }} interval={4} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(0,47,167,0.06)" }}
                    labelFormatter={(d) => d.split("-").reverse().join("/")}
                    formatter={(v) => [v, "Επισκέψεις"]}
                    contentStyle={{ borderRadius: 2, border: "1px solid #E5E7EB", fontSize: 12 }}
                  />
                  <Bar dataKey="count" fill="#002FA7" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {/* Contacts */}
        <section className="lg:col-span-2 space-y-6">
          <div>
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Λίστα Επαφών</div>
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Επαφές ({contacts.length})</h2>
          </div>

          <form onSubmit={addContact} data-testid="add-contact-form" className="bg-white border border-border rounded-sm p-4 space-y-3">
            <input data-testid="contact-name-input" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} className={inputCls} placeholder="Όνομα (προαιρετικό)" />
            <input data-testid="contact-email-input" value={newC.email} onChange={(e) => setNewC({ ...newC, email: e.target.value })} className={inputCls} placeholder="Email" />
            <input data-testid="contact-phone-input" value={newC.phone} onChange={(e) => setNewC({ ...newC, phone: e.target.value })} className={inputCls} placeholder="Τηλέφωνο (+30…)" />
            <button data-testid="add-contact-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 border border-primary text-primary font-mono-accent text-xs uppercase tracking-widest px-4 py-3 rounded-sm hover:bg-primary hover:text-white transition-colors">
              <Plus className="h-4 w-4" /> Προσθήκη επαφής
            </button>
          </form>

          <div className="flex flex-wrap gap-1.5" data-testid="contact-filters">
            {CONTACT_FILTERS.map(([f, l]) => (
              <button key={f} type="button" data-testid={`contact-filter-${f}`} onClick={() => setContactFilter(f)} className={`h-8 px-3 rounded-sm border font-mono-accent text-[10px] uppercase tracking-widest transition-colors ${contactFilter === f ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}>
                {l} <span className={contactFilter === f ? "text-white/70" : "text-muted-foreground"}>({countFor(f)})</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between text-xs">
            <button data-testid="contacts-select-all" onClick={selectAll} className="font-mono-accent uppercase tracking-widest text-primary hover:text-accent">Επιλογή {contactFilter === "all" ? "όλων" : "εμφανιζόμενων"} ({visibleContacts.length})</button>
            <button data-testid="contacts-clear" onClick={clearAll} className="font-mono-accent uppercase tracking-widest text-muted-foreground hover:text-foreground">Καθαρισμός</button>
          </div>

          <div className="bg-white border border-border rounded-sm divide-y divide-border max-h-[420px] overflow-y-auto" data-testid="contacts-list">
            {visibleContacts.length === 0 && <p className="p-4 text-sm text-muted-foreground">{contacts.length === 0 ? "Δεν υπάρχουν επαφές ακόμη." : "Καμία επαφή σε αυτή την κατηγορία."}</p>}
            {visibleContacts.map((c) => (
              <label key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/50 transition-colors">
                <Checkbox checked={!!selected[c.id]} onCheckedChange={() => toggle(c.id)} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate flex items-center gap-2">{c.name || c.email || c.phone}{c.source && <span data-testid={`contact-source-${c.id}`} className="font-mono-accent text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground">{c.source}</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</div>
                </div>
                <button type="button" onClick={() => removeContact(c.id)} data-testid={`delete-contact-${c.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </label>
            ))}
          </div>
        </section>

        {/* Broadcast */}
        <section className="lg:col-span-3">
          <div className="mb-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Email & SMS</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Αποστολή Ανακοίνωσης</h1>
            <p className="mt-2 text-sm text-muted-foreground">Επιλεγμένοι παραλήπτες: <strong className="text-foreground">{selectedContacts.length}</strong> από τις επαφές.</p>
          </div>

          <form onSubmit={onSend} data-testid="admin-broadcast-form" className="bg-white border border-border rounded-sm p-6 space-y-6">
            <div className="flex flex-wrap gap-4">
              <label data-testid="channel-email" className={`flex items-center gap-3 border rounded-sm px-4 py-3 cursor-pointer transition-colors ${email ? "border-primary bg-primary/5" : "border-border"}`}>
                <Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} />
                <Mail className="h-4 w-4 text-primary" /> <span className="text-sm font-medium">Email</span>
              </label>
              <label data-testid="channel-sms" className={`flex items-center gap-3 border rounded-sm px-4 py-3 cursor-pointer transition-colors ${sms ? "border-primary bg-primary/5" : "border-border"}`}>
                <Checkbox checked={sms} onCheckedChange={(v) => setSms(!!v)} />
                <MessageSquare className="h-4 w-4 text-primary" /> <span className="text-sm font-medium">SMS</span>
              </label>
            </div>

            {email && (
              <div>
                <label className={labelCls}>Θέμα email</label>
                <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="π.χ. Πρόγραμμα διαγωνισμάτων" />
              </div>
            )}

            <div>
              <label className={labelCls}>Μήνυμα *</label>
              <textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className={`mt-2 ${inputCls} resize-none`} placeholder="Γράψτε εδώ το μήνυμα..." />
              {sms && <p className="mt-1 text-xs text-muted-foreground">Για SMS συνιστάται σύντομο κείμενο (~160 χαρακτήρες).</p>}
            </div>

            {email && (
              <div>
                <label className={labelCls}>Επιπλέον email (προαιρετικά, εκτός επαφών)</label>
                <textarea data-testid="broadcast-emails" value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)} rows={2} className={`mt-2 ${inputCls} resize-none`} placeholder="a@example.com, b@example.com" />
              </div>
            )}
            {sms && (
              <div>
                <label className={labelCls}>Επιπλέον τηλέφωνα (προαιρετικά, εκτός επαφών)</label>
                <textarea data-testid="broadcast-phones" value={extraPhones} onChange={(e) => setExtraPhones(e.target.value)} rows={2} className={`mt-2 ${inputCls} resize-none`} placeholder="+306900000000" />
              </div>
            )}

            <button data-testid="broadcast-submit" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-accent text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-accent transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Αποστολή..." : "Αποστολή"}
            </button>
          </form>

          {result && (
            <div data-testid="broadcast-result" className="mt-6 bg-white border border-border rounded-sm p-6 grid sm:grid-cols-2 gap-4">
              <ResultCard title="Email" icon={Mail} data={result.email} />
              <ResultCard title="SMS" icon={MessageSquare} data={result.sms} />
            </div>
          )}
        </section>

        {/* Events / Calendar */}
        <section className="lg:col-span-5">
          <div className="mb-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Ημερολόγιο</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" />Εκδηλώσεις & Διαγωνίσματα</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={addEvent} data-testid="add-event-form" className="bg-white border border-border rounded-sm p-5 space-y-4 h-fit">
              <div>
                <label className={labelCls}>Τίτλος *</label>
                <input data-testid="event-title" value={newE.title} onChange={(e) => setNewE({ ...newE, title: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="π.χ. Διαγώνισμα προσομοίωσης" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Ημερομηνία *</label>
                  <input data-testid="event-date" type="date" value={newE.date} onChange={(e) => setNewE({ ...newE, date: e.target.value })} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label className={labelCls}>Τύπος</label>
                  <Select value={newE.type} onValueChange={(v) => setNewE({ ...newE, type: v })}>
                    <SelectTrigger data-testid="event-type" className="mt-2 rounded-sm border-border h-[46px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(EVENT_TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Σημείωση</label>
                <input data-testid="event-note" value={newE.note} onChange={(e) => setNewE({ ...newE, note: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="Προαιρετικό" />
              </div>
              <button data-testid="add-event-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 border border-primary text-primary font-mono-accent text-xs uppercase tracking-widest px-4 py-3 rounded-sm hover:bg-primary hover:text-white transition-colors">
                <Plus className="h-4 w-4" /> Προσθήκη εκδήλωσης
              </button>
            </form>

            <div className="bg-white border border-border rounded-sm divide-y divide-border max-h-[380px] overflow-y-auto">
              {events.length === 0 && <p className="p-4 text-sm text-muted-foreground">Δεν υπάρχουν εκδηλώσεις.</p>}
              {events.map((ev) => (
                <div key={ev.id} data-testid={`event-row-${ev.id}`} className="flex items-center gap-3 p-3">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: EVENT_TYPES[ev.type] || "#6B7280" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{ev.title}</div>
                    <div className="text-xs text-muted-foreground">{ev.date.split("-").reverse().join("/")} · {ev.type}</div>
                  </div>
                  <button type="button" onClick={() => removeEvent(ev.id)} data-testid={`delete-event-${ev.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Educational News */}
        <section className="lg:col-span-5">
          <div className="mb-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Εκπαιδευτικά Νέα</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Newspaper className="h-6 w-6 text-primary" />Άρθρα & Ανακοινώσεις εκπαίδευσης</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <form onSubmit={addEduNews} data-testid="add-edunews-form" className="bg-white border border-border rounded-sm p-5 space-y-4 h-fit">
              <div>
                <label className={labelCls}>Τίτλος *</label>
                <input data-testid="edunews-title" value={newEN.title} onChange={(e) => setNewEN({ ...newEN, title: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="π.χ. Τι αλλάζει στις Πανελλήνιες" />
              </div>
              <div>
                <label className={labelCls}>Περίληψη</label>
                <textarea data-testid="edunews-summary" value={newEN.summary} onChange={(e) => setNewEN({ ...newEN, summary: e.target.value })} rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="Σύντομη περιγραφή του άρθρου" />
              </div>
              <div>
                <label className={labelCls}>Σύνδεσμος (URL)</label>
                <input data-testid="edunews-url" value={newEN.url} onChange={(e) => setNewEN({ ...newEN, url: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://..." />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Πηγή</label>
                  <input data-testid="edunews-source" value={newEN.source} onChange={(e) => setNewEN({ ...newEN, source: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="π.χ. esos.gr" />
                </div>
                <div>
                  <label className={labelCls}>Κατηγορία</label>
                  <Select value={newEN.category} onValueChange={(v) => setNewEN({ ...newEN, category: v })}>
                    <SelectTrigger data-testid="edunews-category" className="mt-2 rounded-sm border-border h-[46px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Νέα">Νέα</SelectItem>
                      <SelectItem value="Συμβουλές">Συμβουλές</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <button data-testid="add-edunews-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 border border-primary text-primary font-mono-accent text-xs uppercase tracking-widest px-4 py-3 rounded-sm hover:bg-primary hover:text-white transition-colors">
                <Plus className="h-4 w-4" /> Προσθήκη άρθρου
              </button>
            </form>

            <div className="bg-white border border-border rounded-sm divide-y divide-border max-h-[420px] overflow-y-auto">
              {eduNews.length === 0 && <p className="p-4 text-sm text-muted-foreground">Δεν υπάρχουν άρθρα.</p>}
              {eduNews.map((n) => (
                <div key={n.id} data-testid={`edunews-row-${n.id}`} className="flex items-start gap-3 p-3">
                  <span className="mt-0.5 font-mono-accent text-[9px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-1 rounded-sm shrink-0">{n.category}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm leading-snug">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      {n.source && <span>{n.source}</span>}
                      {n.url && <a href={n.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-3 w-3" />σύνδεσμος</a>}
                    </div>
                  </div>
                  <button type="button" onClick={() => removeEduNews(n.id)} data-testid={`delete-edunews-${n.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Appointments */}
        <section className="lg:col-span-5" data-testid="admin-appointments">
          <div className="mb-6">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Ραντεβού Προσανατολισμού</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><CalendarCheck className="h-6 w-6 text-primary" />Κρατήσεις συναντήσεων ({appts.filter((a) => a.status === "confirmed").length} ενεργές)</h2>
          </div>
          <div className="bg-white border border-border rounded-sm divide-y divide-border">
            {appts.length === 0 && <p data-testid="admin-appts-empty" className="p-6 text-sm text-muted-foreground">Δεν υπάρχουν ραντεβού ακόμη.</p>}
            {appts.map((a) => (
              <div key={a.id} data-testid={`admin-appt-${a.id}`} className={`p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${a.status !== "confirmed" ? "opacity-50" : ""}`}>
                <div className="sm:w-40 shrink-0">
                  <div className="font-heading font-extrabold text-lg leading-none">{a.date.split("-").reverse().join("/")}</div>
                  <div className="font-mono-accent text-xs text-primary mt-1">{a.time} · 45'</div>
                </div>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-medium">{a.name} {a.student_grade && <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-secondary px-2 py-0.5 rounded-sm ml-1">{a.student_grade}</span>}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">{a.phone} · {a.email}{a.notes ? ` · ${a.notes}` : ""}</div>
                </div>
                {a.status === "confirmed" ? (
                  <button data-testid={`admin-appt-cancel-${a.id}`} onClick={() => cancelAppt(a.id)} className="inline-flex items-center gap-1.5 font-mono-accent text-[10px] uppercase tracking-widest text-red-700 hover:underline shrink-0"><XCircle className="h-4 w-4" /> Ακύρωση</button>
                ) : (
                  <span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Ακυρωμένο</span>
                )}
              </div>
            ))}
          </div>
        </section>

        <AdminMembers authHeader={authHeader} onContactsChanged={loadContacts} />
        <AdminTeacherEval authHeader={authHeader} />
        <AdminQuiz authHeader={authHeader} />
        <AdminQr />
      </main>
    </div>
  );
}

function StatCard({ label, value, unique, testid }) {
  const fmt = (v) => (v === undefined || v === null ? "—" : v.toLocaleString("el-GR"));
  return (
    <div data-testid={testid} className="bg-white border border-border rounded-sm p-5">
      <div className="flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">
        <Eye className="h-3.5 w-3.5 text-primary" /> {label}
      </div>
      <div className="font-heading text-3xl font-extrabold text-primary mt-2">{fmt(value)}</div>
      <div className="text-xs text-muted-foreground mt-1" data-testid={`${testid}-unique`}>
        {fmt(unique)} μοναδικοί
      </div>
    </div>
  );
}

function ResultCard({ title, icon: Icon, data }) {
  const skippedMsg = {
    missing_resend_key: "Λείπει το Resend API key",
    missing_twilio_credentials: "Λείπουν τα στοιχεία Twilio",
  };
  return (
    <div className="border border-border rounded-sm p-4">
      <div className="flex items-center gap-2 font-heading font-bold"><Icon className="h-4 w-4 text-primary" />{title}</div>
      {data.skipped ? (
        <p className="mt-2 text-sm text-accent">{skippedMsg[data.skipped] || "Παραλείφθηκε"}</p>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          Εστάλησαν: <strong className="text-foreground">{data.sent}</strong> · Απέτυχαν: <strong className="text-foreground">{data.failed}</strong>
        </p>
      )}
    </div>
  );
}
